import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

/**
 * FR-109 (v1.9): Context for session guard to intercept logout during active sessions.
 * ProblemsPage sets the guard when an active session exists, and Header checks it before logout.
 */
interface SessionGuardContextValue {
  hasActiveSession: boolean;
  setHasActiveSession: (value: boolean) => void;
  logoutInterceptor: (() => void) | null;
  setLogoutInterceptor: (interceptor: (() => void) | null) => void;
  scoringType: string | null;
  setScoringType: (value: string | null) => void;
  /**
   * What the running session calls itself, for the site header's phone banner (feature 023) — e.g.
   * "Beginner · Bidding Practice". The session owns this because only it knows the level the learner
   * chose; the header used to keep its own copy of the mode→label mapping and the two could disagree
   * the moment either changed. Null when no session is running, which is the header's cue to draw
   * its usual brand.
   */
  sessionTitle: string | null;
  setSessionTitle: (value: string | null) => void;
  /**
   * An optional muted line ABOVE `sessionTitle` in the phone banner, for a session whose name has
   * a place-in-the-course to it — "Competitive Bidding" over "The overcalling side". It is the
   * same two-part shape module practice draws from the URL (`modulePracticeHeaderTitle`), and it
   * renders through the same `.module-trail` grid, so the two surfaces read alike on a phone.
   * Null for a session that names itself in one line, which is most of them.
   */
  sessionTitleTrail: string | null;
  setSessionTitleTrail: (value: string | null) => void;
}

const SessionGuardContext = createContext<SessionGuardContextValue | undefined>(undefined);

export function SessionGuardProvider({ children }: { children: ReactNode }) {
  const [hasActiveSession, setHasActiveSession] = useState(false);
  const [logoutInterceptor, setLogoutInterceptorState] = useState<(() => void) | null>(null);
  const [scoringType, setScoringType] = useState<string | null>(null);
  const [sessionTitle, setSessionTitle] = useState<string | null>(null);
  const [sessionTitleTrail, setSessionTitleTrail] = useState<string | null>(null);

  const setLogoutInterceptor = useCallback((interceptor: (() => void) | null) => {
    setLogoutInterceptorState(() => interceptor);
  }, []);

  return (
    <SessionGuardContext.Provider
      value={{
        hasActiveSession,
        setHasActiveSession,
        logoutInterceptor,
        setLogoutInterceptor,
        scoringType,
        setScoringType,
        sessionTitle,
        setSessionTitle,
        sessionTitleTrail,
        setSessionTitleTrail,
      }}
    >
      {children}
    </SessionGuardContext.Provider>
  );
}

export function useSessionGuard(): SessionGuardContextValue {
  const context = useContext(SessionGuardContext);
  if (context === undefined) {
    throw new Error('useSessionGuard must be used within a SessionGuardProvider');
  }
  return context;
}
